import { useEffect, useState, useCallback } from "react";
import { ClipboardList, Check, X, Loader2, AlertTriangle } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { api, apiErr } from "../../lib/api";
import { Loader, Empty, GoldButton, GhostButton } from "../../components/UI";
import { ClubCrest } from "../../components/ClubCrest";
import { toast } from "sonner";

const TABS = [
  { key: "PENDING", label: "Pending" },
  { key: "DECIDED", label: "Decided" },
  { key: "ALL", label: "All" },
];

const STATUS_STYLE = {
  PENDING: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  APPROVED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  REJECTED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  CANCELLED: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${STATUS_STYLE[status] || "bg-slate-700/30 text-slate-300 border-slate-600"}`} data-testid={`entry-status-${status}`}>
      {status}
    </span>
  );
}

function RejectModal({ req, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A rejection reason is required."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/entry-requests/${req.id}/reject`, { reason: reason.trim() });
      toast.success("Entry request rejected");
      onDone();
    } catch (e) { toast.error(apiErr(e, "Failed to reject")); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="entry-reject-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.25)] p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-rose-400" /> Reject Entry
        </h3>
        <p className="text-sm text-slate-400 mt-1 break-words">Rejecting entry of “{req.club?.name}” into “{req.competition?.name}”. The owner will see this reason.</p>
        <textarea data-testid="entry-reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="Explain why this entry is rejected…"
          className="w-full mt-4 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60 resize-none" />
        <div className="flex justify-end gap-2 mt-4">
          <GhostButton onClick={onClose} data-testid="entry-reject-cancel">Cancel</GhostButton>
          <button onClick={submit} disabled={busy || !reason.trim()} data-testid="entry-reject-confirm"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed" style={{ transition: "background-color 0.15s ease" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Reject
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ r, onApprove, onReject, busyId }) {
  const busy = busyId === r.id;
  const when = r.created_at ? new Date(r.created_at).toLocaleDateString() : "—";
  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`entry-request-${r.id}`}>
      <ClubCrest club={r.club} size={40} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-heading text-base font-bold uppercase tracking-tight text-slate-50 break-words">{r.club?.name}</span>
          <span className="text-slate-500 text-xs">→</span>
          <span className="text-slate-200 text-sm break-words">{r.competition?.name}</span>
          <StatusPill status={r.status} />
        </div>
        <div className="text-xs text-slate-400 font-mono mt-1 break-words">
          {r.club?.wsbf_club_id || "—"}<span className="text-slate-600"> · </span>{r.competition?.season_label}<span className="text-slate-600"> · </span>{when}
        </div>
        {r.status === "REJECTED" && r.reason && <div className="text-xs text-rose-300/90 mt-1 break-words" data-testid={`entry-reason-${r.id}`}>Reason: {r.reason}</div>}
      </div>
      {r.status === "PENDING" && (
        <div className="flex gap-2 shrink-0">
          <GoldButton onClick={() => onApprove(r)} disabled={busy} data-testid={`entry-approve-${r.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve
          </GoldButton>
          <button onClick={() => onReject(r)} disabled={busy} data-testid={`entry-reject-${r.id}`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-rose-500/40 text-rose-300 hover:border-rose-400 hover:text-rose-200 disabled:opacity-40" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
            <X className="w-4 h-4" /> Reject
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminEntryRequests() {
  const [tab, setTab] = useState("PENDING");
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = useCallback(async (status) => {
    setRows(null);
    try { const { data } = await api.get("/admin/entry-requests", { params: { status, page: 1, page_size: 100 } }); setRows(data.requests || []); }
    catch (e) { toast.error(apiErr(e)); setRows([]); }
  }, []);
  useEffect(() => { load(tab); }, [tab, load]);

  const approve = async (r) => {
    if (!window.confirm(`Approve entry of “${r.club?.name}” into “${r.competition?.name}”?`)) return;
    setBusyId(r.id);
    try { await api.post(`/admin/entry-requests/${r.id}/approve`); toast.success("Entry approved — club added"); load(tab); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-entry-requests-page">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">Control Center</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-[#E5B842] shrink-0" /> Entry Requests
          </h1>
          <p className="text-slate-400 mt-2 text-sm sm:text-base">Owners requesting to enter clubs into competitions. Approving adds the club.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="entry-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`entry-tab-${t.key.toLowerCase()}`}
              className={`px-4 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wide ${tab === t.key ? "bg-[#E5B842] text-black" : "bg-[#161616] border border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-[#E5B842]"}`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
              {t.label}
            </button>
          ))}
        </div>

        {rows === null ? <Loader label="Loading requests…" />
          : rows.length === 0 ? <Empty label={`No ${tab === "ALL" ? "" : tab.toLowerCase() + " "}entry requests`} />
          : (
            <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] divide-y divide-[rgba(212,175,55,0.10)]" data-testid="entry-list">
              {rows.map((r) => <Row key={r.id} r={r} onApprove={approve} onReject={setRejecting} busyId={busyId} />)}
            </div>
          )}
      </div>
      {rejecting && <RejectModal req={rejecting} onClose={() => setRejecting(null)} onDone={() => { setRejecting(null); load(tab); }} />}
    </AdminLayout>
  );
}
